<script setup>
import { Check, ArrowRight } from 'lucide-vue-next'
</script>

<template>
  <div class="hero-visual">
    <div class="viz">
      <!-- Document panel — tilted invoice with animated scan line and floating confidence pills -->
      <div class="viz-doc" aria-label="Sample invoice">
        <div class="viz-doc-head">
          <div>
            <div class="viz-doc-co">Northwind Trading Co.</div>
            <div class="viz-doc-meta">
              408 Spear St, San Francisco<br />
              support@northwind.co
            </div>
          </div>
          <div class="text-right">
            <div class="viz-doc-label">Invoice</div>
            <div class="viz-doc-num">#INV-2042</div>
          </div>
        </div>

        <div class="viz-doc-bill">
          Bill to <strong>Acme Bookkeeping LLC</strong>
        </div>

        <div class="viz-doc-table">
          <div class="viz-doc-row head">
            <span>Description</span>
            <span class="num">Qty</span>
            <span class="num">Total</span>
          </div>
          <div class="viz-doc-row">
            <span>Annual subscription</span>
            <span class="num">1</span>
            <span class="num">$1,200.00</span>
          </div>
          <div class="viz-doc-row">
            <span>Onboarding setup</span>
            <span class="num">1</span>
            <span class="num">$340.00</span>
          </div>
          <div class="viz-doc-row">
            <span>Premium support</span>
            <span class="num">2</span>
            <span class="num">$120.00</span>
          </div>
        </div>

        <div class="viz-doc-totals">
          <div class="row"><span>Subtotal</span><span class="num">$1,660.00</span></div>
          <div class="row"><span>Tax (8.5%)</span><span class="num">$141.10</span></div>
          <div class="row total"><span>Total due</span><span class="num">$1,801.10</span></div>
        </div>

        <span class="viz-confidence c1">
          <Check class="h-2.5 w-2.5" :stroke-width="3" />
          99%
        </span>
        <span class="viz-confidence c2">
          <Check class="h-2.5 w-2.5" :stroke-width="3" />
          97%
        </span>
      </div>

      <!-- Connector + pulse -->
      <div class="viz-bridge" aria-hidden="true">
        <svg viewBox="0 0 56 200" preserveAspectRatio="none">
          <defs>
            <linearGradient id="papersnap-conn" x1="0" x2="1" y1="0.5" y2="0.5">
              <stop offset="0%" stop-color="rgb(var(--color-primary))" stop-opacity="0" />
              <stop offset="50%" stop-color="rgb(var(--color-primary))" stop-opacity="0.7" />
              <stop offset="100%" stop-color="rgb(var(--color-primary))" stop-opacity="0" />
            </linearGradient>
          </defs>
          <path d="M 0 100 C 18 100, 38 100, 56 100" stroke="url(#papersnap-conn)" stroke-width="1.5" fill="none" stroke-dasharray="4 4" />
        </svg>
        <span class="pulse">
          <ArrowRight class="h-4 w-4" :stroke-width="2.4" />
        </span>
      </div>

      <!-- JSON panel — tabs + status + line-by-line fade-in -->
      <div class="viz-json" aria-label="Extracted JSON">
        <div class="viz-json-head">
          <div class="viz-json-tabs">
            <span class="viz-json-tab active">JSON</span>
            <span class="viz-json-tab">CSV</span>
            <span class="viz-json-tab">XLSX</span>
          </div>
          <span class="viz-json-status">
            <span class="dot" />
            11.4s
          </span>
        </div>
        <div class="viz-json-body">
          <span class="viz-json-line"><span class="tok-brace">{</span></span>
          <span class="viz-json-line">  <span class="tok-key">"doc_type"</span><span class="tok-punc">:</span> <span class="tok-str">"invoice"</span><span class="tok-punc">,</span></span>
          <span class="viz-json-line">  <span class="tok-key">"invoice_number"</span><span class="tok-punc">:</span> <span class="tok-str">"INV-2042"</span><span class="tok-punc">,</span></span>
          <span class="viz-json-line">  <span class="tok-key">"vendor"</span><span class="tok-punc">:</span> <span class="tok-str">"Northwind Trading Co."</span><span class="tok-punc">,</span></span>
          <span class="viz-json-line">  <span class="tok-key">"bill_to"</span><span class="tok-punc">:</span> <span class="tok-str">"Acme Bookkeeping LLC"</span><span class="tok-punc">,</span></span>
          <span class="viz-json-line">  <span class="tok-key">"line_items"</span><span class="tok-punc">:</span> <span class="tok-brace">[</span></span>
          <span class="viz-json-line">    <span class="tok-brace">{</span> <span class="tok-key">"desc"</span><span class="tok-punc">:</span> <span class="tok-str">"Annual subscription"</span><span class="tok-punc">,</span> <span class="tok-key">"qty"</span><span class="tok-punc">:</span> <span class="tok-num">1</span><span class="tok-punc">,</span> <span class="tok-key">"total"</span><span class="tok-punc">:</span> <span class="tok-num">1200.00</span> <span class="tok-brace">},</span></span>
          <span class="viz-json-line">    <span class="tok-brace">{</span> <span class="tok-key">"desc"</span><span class="tok-punc">:</span> <span class="tok-str">"Onboarding setup"</span><span class="tok-punc">,</span>     <span class="tok-key">"qty"</span><span class="tok-punc">:</span> <span class="tok-num">1</span><span class="tok-punc">,</span> <span class="tok-key">"total"</span><span class="tok-punc">:</span> <span class="tok-num">340.00</span>  <span class="tok-brace">},</span></span>
          <span class="viz-json-line">    <span class="tok-brace">{</span> <span class="tok-key">"desc"</span><span class="tok-punc">:</span> <span class="tok-str">"Premium support"</span><span class="tok-punc">,</span>      <span class="tok-key">"qty"</span><span class="tok-punc">:</span> <span class="tok-num">2</span><span class="tok-punc">,</span> <span class="tok-key">"total"</span><span class="tok-punc">:</span> <span class="tok-num">120.00</span>  <span class="tok-brace">}</span></span>
          <span class="viz-json-line">  <span class="tok-brace">],</span></span>
          <span class="viz-json-line">  <span class="tok-key">"subtotal"</span><span class="tok-punc">:</span> <span class="tok-num">1660.00</span><span class="tok-punc">,</span></span>
          <span class="viz-json-line">  <span class="tok-key">"tax"</span><span class="tok-punc">:</span> <span class="tok-num">141.10</span><span class="tok-punc">,</span></span>
          <span class="viz-json-line">  <span class="tok-key">"total_due"</span><span class="tok-punc">:</span> <span class="tok-num">1801.10</span></span>
          <span class="viz-json-line"><span class="tok-brace">}</span></span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Design tokens — direct port of papersnap-home.css. Light + dark sets. */
.hero-visual {
  --psnap-surface-card:   #ffffff;
  --psnap-surface-muted:  rgb(241 245 249);
  --psnap-border-soft:    rgb(226 232 240);
  --psnap-fg-1: rgb(15 23 42);
  --psnap-fg-2: rgb(71 85 105);
  --psnap-fg-3: rgb(100 116 139);
  --psnap-fg-4: rgb(148 163 184);

  position: relative;
  aspect-ratio: 1 / 0.92;
  display: flex;
  align-items: center;
  justify-content: center;
}
:global(html.dark) .hero-visual {
  --psnap-surface-card:   rgb(15 23 42);
  --psnap-surface-muted:  rgb(30 41 59);
  --psnap-border-soft:    rgb(30 41 59);
  --psnap-fg-1: #ffffff;
  --psnap-fg-2: rgb(203 213 225);
  --psnap-fg-3: rgb(148 163 184);
  --psnap-fg-4: rgb(100 116 139);
}

.viz {
  position: relative;
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: 1fr 56px 1fr;
  align-items: center;
  gap: 0;
}

.viz-doc,
.viz-json {
  background: var(--psnap-surface-card);
  border: 1px solid var(--psnap-border-soft);
  border-radius: 14px;
  box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.10), 0 8px 10px -6px rgb(0 0 0 / 0.08);
  overflow: hidden;
  position: relative;
  transition: transform 0.4s ease;
}

.viz-doc {
  padding: 18px 16px;
  transform: rotate(-2deg);
}

.viz-doc::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  height: 6px;
  background: linear-gradient(90deg, rgb(var(--color-primary)) 0%, rgb(var(--color-secondary)) 100%);
}

.viz-doc-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.viz-doc-co {
  font-family: 'Poppins', system-ui, sans-serif;
  font-weight: 700;
  font-size: 13px;
  color: var(--psnap-fg-1);
}

.viz-doc-meta {
  font-size: 9px;
  color: var(--psnap-fg-3);
  line-height: 1.5;
}

.viz-doc-label {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: rgb(var(--color-primary));
  text-transform: uppercase;
}

.viz-doc-num {
  font-size: 10px;
  color: var(--psnap-fg-2);
  margin-top: 2px;
  font-family: 'JetBrains Mono', ui-monospace, monospace;
}

.viz-doc-bill {
  font-size: 9px;
  color: var(--psnap-fg-3);
  margin-bottom: 10px;
}
.viz-doc-bill strong {
  display: block;
  color: var(--psnap-fg-1);
  font-weight: 600;
  font-size: 10px;
  margin-top: 1px;
}

.viz-doc-table {
  border-top: 1px solid var(--psnap-border-soft);
  border-bottom: 1px solid var(--psnap-border-soft);
  margin: 10px 0;
}

.viz-doc-row {
  display: grid;
  grid-template-columns: 1fr 28px 38px;
  gap: 8px;
  font-size: 9px;
  padding: 5px 0;
  color: var(--psnap-fg-2);
  border-bottom: 1px dashed rgb(226 232 240 / 0.7);
}
:global(html.dark) .viz-doc-row {
  border-bottom-color: rgb(51 65 85 / 0.7);
}
.viz-doc-row:last-child {
  border-bottom: 0;
}
.viz-doc-row.head {
  font-size: 8px;
  font-weight: 700;
  color: var(--psnap-fg-3);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.viz-doc-row .num {
  text-align: right;
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  color: var(--psnap-fg-1);
}

.viz-doc-totals {
  display: grid;
  gap: 3px;
  font-size: 9px;
  margin-top: 8px;
}
.viz-doc-totals .row {
  display: flex;
  justify-content: space-between;
  color: var(--psnap-fg-3);
}
.viz-doc-totals .row.total {
  margin-top: 4px;
  padding-top: 6px;
  border-top: 1px solid var(--psnap-border-soft);
  font-weight: 700;
  font-size: 11px;
  color: var(--psnap-fg-1);
  font-family: 'JetBrains Mono', ui-monospace, monospace;
}
.viz-doc-totals .num {
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  color: var(--psnap-fg-1);
}

/* Animated scan line over the document */
.viz-doc::after {
  content: '';
  position: absolute;
  left: -2px;
  right: -2px;
  height: 14%;
  background: linear-gradient(180deg,
    rgb(var(--color-primary) / 0) 0%,
    rgb(var(--color-primary) / 0.18) 50%,
    rgb(var(--color-primary) / 0) 100%);
  border-top: 1px solid rgb(var(--color-primary) / 0.6);
  border-bottom: 1px solid rgb(var(--color-primary) / 0.6);
  box-shadow: 0 0 24px rgb(var(--color-primary) / 0.5);
  animation: papersnap-scan 3.6s cubic-bezier(0.65, 0, 0.35, 1) infinite;
  pointer-events: none;
}
@keyframes papersnap-scan {
  0% {
    top: -10%;
    opacity: 0;
  }
  10% { opacity: 1; }
  90% { opacity: 1; }
  100% {
    top: 100%;
    opacity: 0;
  }
}

/* Connector */
.viz-bridge {
  position: relative;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.viz-bridge svg {
  width: 100%;
  height: 100%;
  position: absolute;
  inset: 0;
  pointer-events: none;
}
.viz-bridge .pulse {
  position: relative;
  width: 32px;
  height: 32px;
  border-radius: 9999px;
  background: rgb(var(--color-primary));
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 0 0 28px rgb(var(--color-primary) / 0.55), inset 0 1px 0 rgb(255 255 255 / 0.3);
  z-index: 2;
}
.viz-bridge .pulse::before {
  content: '';
  position: absolute;
  inset: -4px;
  border-radius: 9999px;
  border: 2px solid rgb(var(--color-primary));
  opacity: 0;
  animation: papersnap-ring 2.2s ease-out infinite;
}
@keyframes papersnap-ring {
  0% {
    transform: scale(0.8);
    opacity: 0.8;
  }
  80% {
    transform: scale(1.8);
    opacity: 0;
  }
  100% {
    transform: scale(1.8);
    opacity: 0;
  }
}

/* JSON panel */
.viz-json {
  transform: rotate(2deg);
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 11px;
  line-height: 1.7;
}
.viz-json-head {
  padding: 9px 14px;
  border-bottom: 1px solid var(--psnap-border-soft);
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgb(248 250 252);
}
:global(html.dark) .viz-json-head {
  background: rgb(2 6 23 / 0.4);
}
.viz-json-tabs {
  display: inline-flex;
  gap: 2px;
}
.viz-json-tab {
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 10px;
  font-weight: 600;
  padding: 4px 9px;
  border-radius: 6px;
  color: var(--psnap-fg-3);
}
.viz-json-tab.active {
  background: var(--psnap-surface-card);
  color: rgb(var(--color-primary));
  border: 1px solid var(--psnap-border-soft);
}
:global(html.dark) .viz-json-tab.active {
  background: rgb(30 41 59);
}
.viz-json-status {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 10px;
  font-weight: 600;
  color: rgb(16 185 129);
}
.viz-json-status .dot {
  width: 6px;
  height: 6px;
  border-radius: 9999px;
  background: rgb(16 185 129);
  box-shadow: 0 0 6px rgb(16 185 129 / 0.6);
}

.viz-json-body {
  padding: 12px 14px;
}
.viz-json-line {
  display: block;
  white-space: nowrap;
  opacity: 0;
  animation: papersnap-fade-up 0.4s ease forwards;
}
.tok-key { color: rgb(var(--color-secondary)); }
.tok-str { color: rgb(16 185 129); }
.tok-num { color: rgb(245 158 11); }
.tok-punc { color: var(--psnap-fg-3); }
.tok-brace { color: var(--psnap-fg-2); }
:global(html.dark) .tok-key { color: rgb(56 189 248); }

.viz-json-line:nth-child(1) { animation-delay: 0.4s; }
.viz-json-line:nth-child(2) { animation-delay: 0.5s; }
.viz-json-line:nth-child(3) { animation-delay: 0.6s; }
.viz-json-line:nth-child(4) { animation-delay: 0.7s; }
.viz-json-line:nth-child(5) { animation-delay: 0.8s; }
.viz-json-line:nth-child(6) { animation-delay: 0.9s; }
.viz-json-line:nth-child(7) { animation-delay: 1.0s; }
.viz-json-line:nth-child(8) { animation-delay: 1.1s; }
.viz-json-line:nth-child(9) { animation-delay: 1.2s; }
.viz-json-line:nth-child(10) { animation-delay: 1.3s; }
.viz-json-line:nth-child(11) { animation-delay: 1.4s; }
.viz-json-line:nth-child(12) { animation-delay: 1.5s; }
.viz-json-line:nth-child(13) { animation-delay: 1.6s; }
.viz-json-line:nth-child(14) { animation-delay: 1.7s; }
@keyframes papersnap-fade-up {
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Floating confidence pills */
.viz-confidence {
  position: absolute;
  z-index: 3;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 9999px;
  background: rgb(16 185 129);
  color: white;
  font-family: 'Inter', system-ui, sans-serif;
  font-weight: 700;
  font-size: 9px;
  letter-spacing: 0.04em;
  box-shadow: 0 4px 14px -4px rgb(16 185 129 / 0.6);
  animation: papersnap-bob 5s ease-in-out infinite;
}
.viz-confidence.c1 {
  top: 17%;
  right: -10px;
  animation-delay: 1.6s;
}
.viz-confidence.c2 {
  top: 49%;
  right: -16px;
  background: rgb(var(--color-primary));
  box-shadow: 0 4px 14px -4px rgb(var(--color-primary) / 0.6);
  animation-delay: 2.1s;
}
@keyframes papersnap-bob {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
}

@media (prefers-reduced-motion: reduce) {
  .viz-doc::after,
  .viz-bridge .pulse::before,
  .viz-confidence,
  .viz-json-line {
    animation: none !important;
  }
  .viz-json-line { opacity: 1; }
}
</style>
